import React, {useState, useRef} from 'react'
import styled from 'styled-components'
import {injectIntl} from 'react-intl'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Popover from '@mui/material/Popover'
import {Clear} from '@mui/icons-material'
import Dialog from 'components/Dialog'
import {compressImage, getFileUrl} from 'utils/utils'

const Img = styled.img`
    max-height: 35px;
    max-width: 50px;
    padding: 0 10px;
    position: absolute;
`

const Input = styled.input`
    opacity: 0;
    width: 0;
`

const Placeholder = styled.span`
    color: #7c9198;
    font-style: italic;
    font-size: 13px;
    padding: 0 10px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`

const Container = styled.span`
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    cursor: ${props => props.readonly ? 'default' : 'pointer'};

    svg {
        display: none;
    }

    :hover {
        svg {
            display: block;
        }
    }
`

const Tabs = styled.div`
    display: flex;
    padding: 16px 0 30px;
`

const Tab = styled.div`
    text-transform: uppercase;
    border-bottom: 2px solid ${props => props.active ? '#415dd4' : 'transparent'};
    transition: border-color .3s cubic-bezier(.55, 0, .1, 1);
    color: ${props => props.active ? '#415dd4' : '#a9b9bd'};
    cursor: pointer;
    padding: 10px;
    width: 50%;
    text-align: center;
    font-size: 12px;
    letter-spacing: 1px;
`

const Image = ({src, readonly, name, handleUpdateValue, intl}) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const [open, setOpen] = useState(false)
    const [type, setType] = useState('file')
    const [value, setValue] = useState('')
    const [file, setFile] = useState({name: '', data: ''})

    const input = useRef(null)

    const handlePopoverOpen = (e) => {
        setAnchorEl(e.currentTarget)
    }

    const handlePopoverClose = () => {
        setAnchorEl(null)
    }

    const handleDialogOpen = () => {
        setOpen(true)
        setType(src && src.indexOf('http') !== -1 ? 'url' : 'file')
    }

    const handleDialogClose = () => {
        setOpen(false)
        setValue('')
        setFile({name: '', data: ''})
    }

    const handleClear = (e) => {
        e.stopPropagation()
        handleUpdateValue('', name)
    }

    const updateValue = () => {
        if (value) {
            handleUpdateValue(value, name)
            handleDialogClose()
        } else {
            compressImage(file.data).then((string) => {
                handleUpdateValue(string, name)
                handleDialogClose()
            })
        }
    }

    const handleDeleteValue = () => {
        handleUpdateValue('', name)
        handleDialogClose()
    }

    const handleDownload = (e) => {
        const data = e.currentTarget.files[0]

        if (data) {
            const reader = new FileReader()
            reader.readAsDataURL(data)
            reader.onload = () => setFile({name: data.name, data: reader.result})
            reader.onerror = (error) => alert(error)
        }
    }

    const getImageUrl = (src) => {
        if (src && src.indexOf('http')) {
            return getFileUrl(src)
        }

        return src
    }

    return (
        <React.Fragment>
            <Container
                readonly={readonly}
                onClick={(e) => readonly ? e.preventDefault : handleDialogOpen()}
            >
                {src ?
                    <Img
                        src={getImageUrl(src)}
                        alt=''
                        onMouseEnter={handlePopoverOpen}
                        onMouseLeave={handlePopoverClose}
                    />
                    :
                    <Placeholder>
                        {readonly ? intl.formatMessage({id: "table.placeholder_image_readonly"}) : intl.formatMessage({id: "table.placeholder_image"})}
                    </Placeholder>
                }
                {src && !readonly &&
                    <Clear onClick={handleClear} style={{ fontSize: 20, margin: '0 10px 0 auto', cursor: 'pointer' }} />
                }
            </Container>
            <Popover
                style={{ pointerEvents: 'none', margin: '0 15px '}}
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                disableRestoreFocus
            >
                <img alt='' style={{ width: '300px', padding: '10px' }} src={getImageUrl(src)} />
            </Popover>
            {open &&
                <Dialog
                    open={open}
                    onClose={handleDialogClose}
                    fullWidth={true}
                >
                    <DialogContent>
                        <Tabs>
                            <Tab active={type === 'file'} onClick={() => setType('file')}>
                                {intl.formatMessage({id: "table.image_upload_file"})}
                            </Tab>
                            <Tab active={type === 'url'} onClick={() => setType('url')}>
                                {intl.formatMessage({id: "table.image_upload_from_url"})}
                            </Tab>
                        </Tabs>
                        {type === 'file' ?
                            <React.Fragment>
                                <TextField variant="standard"
                                    label={intl.formatMessage({id: "table.path_to_local_file"})}
                                    placeholder={intl.formatMessage({id: "table.click_to_upload"})}
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    onClick={() => input.current.click()}
                                    value={file.name}
                                />
                                <Input type='file'
                                    onChange={handleDownload}
                                    ref={input}
                                    accept="image/jpeg, image/png, image/gif"
                                />
                            </React.Fragment> :
                            <TextField variant="standard"
                                label={intl.formatMessage({id: "table.image_url"})}
                                placeholder={intl.formatMessage({id: "table.enter_url_to_file"})}
                                fullWidth
                                InputLabelProps={{
                                    shrink: true
                                }}
                                defaultValue={src && src.indexOf('http') !== -1 ? src : ''}
                                onChange={(e) => setValue(e.currentTarget.value)}
                            />
                        }
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose} color="primary">
                            {intl.formatMessage({id: "app.confirm_cancel"})}
                        </Button>
                        {src &&
                            <Button onClick={handleDeleteValue} variant="contained" color="secondary">
                                {intl.formatMessage({id: "app.confirm_delete"})}
                            </Button>
                        }
                        <Button variant="contained"
                            color="primary"
                            onClick={updateValue}
                            disabled={!value && !file.name}
                        >
                            {intl.formatMessage({id: "app.confirm_save"})}
                        </Button>
                    </DialogActions>
                </Dialog>
            }
        </React.Fragment>
    )
}

export default injectIntl(Image)
